import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { X, Gift, Loader2, Copy, Check, Users, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReferralModal = ({ token, onClose }) => {
  const { t } = useLang();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/referral`, auth)
      .then(({ data }) => setData(data))
      .catch((e) => { console.error("load referral failed:", e); setData(null); });
  }, []); // eslint-disable-line -- run once on mount; auth token is stable per session

  const link = data ? `${window.location.origin}/?ref=${data.code}` : "";

  const copy = async () => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success(t.ref_copied);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" data-testid="referral-modal">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-[#F4F0E6] border-[4px] border-black rounded-2xl shadow-[8px_8px_0px_0px_#000] my-8"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b-[3px] border-black">
          <h2 className="font-display font-bold text-xl uppercase tracking-tight flex items-center gap-2">
            <Gift size={20} strokeWidth={2.5} /> {t.ref_title}
          </h2>
          <button data-testid="referral-close-btn" onClick={onClose} className="border-[2px] border-black rounded-full p-1 bg-white hover:bg-[#FFD500] transition-colors">
            <X size={18} strokeWidth={2.5} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {data === null && (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin" size={26} /></div>
          )}

          {data && (
            <>
              <p className="text-sm font-medium text-[#444]">
                {t.ref_desc.replace("{n}", data.reward).replace("{b}", data.signup_bonus)}
              </p>

              <div>
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#444] mb-2">{t.ref_link_label}</p>
                <div className="flex items-stretch gap-2">
                  <input
                    data-testid="referral-link"
                    readOnly
                    value={link}
                    onFocus={(e) => e.target.select()}
                    className="flex-1 min-w-0 bg-white border-[3px] border-black rounded-xl px-3 py-2.5 text-sm font-mono2 font-semibold focus:outline-none"
                  />
                  <button
                    data-testid="referral-copy-btn"
                    onClick={copy}
                    className="shrink-0 flex items-center gap-1.5 bg-[#00FFA3] border-[3px] border-black rounded-xl px-4 font-bold uppercase text-sm shadow-[3px_3px_0px_0px_#000] transition-[transform,box-shadow] duration-100 hover:shadow-[1px_1px_0px_0px_#000] hover:translate-x-[2px] hover:translate-y-[2px]"
                  >
                    {copied ? <Check size={16} strokeWidth={3} /> : <Copy size={16} strokeWidth={2.5} />}
                    {copied ? t.ref_copied_short : t.ref_copy}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="border-[2px] border-black rounded-xl bg-[#FFD500] p-4 text-center">
                  <Users size={20} strokeWidth={2.5} className="mx-auto mb-1" />
                  <p className="font-mono2 font-bold text-3xl leading-none">{data.count}</p>
                  <p className="text-[10px] font-bold uppercase tracking-wide mt-1">{t.ref_stats_friends}</p>
                </div>
                <div className="border-[2px] border-black rounded-xl bg-[#00FFA3] p-4 text-center">
                  <Sparkles size={20} strokeWidth={2.5} className="mx-auto mb-1" />
                  <p className="font-mono2 font-bold text-3xl leading-none">{data.credits_earned}</p>
                  <p className="text-[10px] font-bold uppercase tracking-wide mt-1">{t.ref_stats_credits}</p>
                </div>
              </div>

              {!data.billing_enabled && (
                <p className="text-xs font-semibold text-[#666] bg-white border-[2px] border-black rounded-xl px-3 py-2.5" data-testid="referral-note">
                  💜 {t.ref_reward_note}
                </p>
              )}
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};
